import { useEffect, useMemo, useRef, useState } from "react";
import {
  Badge,
  Button,
  Group,
  Loader,
  Stack,
  Table,
  Tabs,
  Text,
} from "@mantine/core";
import { useServerApi } from "../hooks/userServerApi";
import { useFormatter } from "../hooks/useFomatter";
import EntityTabAnchor from "../pages/account/payroll2/EntityTabAnchor";
import { getPayrollLabel } from "../helpers/payrollBidDisplay";

const PAGE_SIZE = 50;
const STATUS_TABS = ["ALL", "PENDING", "HOLD", "SETTLED", "CANCEL"];

const getStatusColor = (status) => {
  switch (status) {
    case "SETTLED":
      return "green";
    case "HOLD":
      return "yellow";
    case "CANCEL":
      return "red";
    case "PENDING":
    default:
      return "gray";
  }
};

const getSourceLabel = (bid) => {
  if (bid.sourceType === "ADJUSTMENT") return "Adjustment";
  if (bid.sourceType === "CLIENTPAYMENT" || bid.debitNote)
    return "Client Payment";
  if (bid.sourceType === "COMMISSIONSTATEMENT" || bid.order)
    return "Comm. Statement";
  if (bid.sourceType === "INCENTIVE" || bid.incentive) return "Incentive/Charge";
  return bid.sourceType || "-";
};

const getSourceColor = (bid) => {
  if (bid.sourceType === "ADJUSTMENT") return "red";
  if (bid.sourceType === "CLIENTPAYMENT" || bid.debitNote) return "blue";
  if (bid.sourceType === "COMMISSIONSTATEMENT" || bid.order) return "violet";
  if (bid.sourceType === "INCENTIVE" || bid.incentive) return "teal";
  return "gray";
};

const buildStatusSummary = (bids) => {
  const counts = { PENDING: 0, HOLD: 0, SETTLED: 0, CANCEL: 0 };
  let totalAmount = 0;

  for (const bid of bids) {
    const status = bid.status || "PENDING";
    if (counts[status] != null) counts[status] += 1;
    totalAmount += bid.commissionAmount ?? 0;
  }

  return { counts, totalAmount, total: bids.length };
};

const buildSearchQuery = (walletId, statusTab) => {
  const searchQuery = { wallet: walletId };
  if (statusTab && statusTab !== "ALL") searchQuery.status = statusTab;
  return searchQuery;
};

const WalletPayrollBids = ({ form }) => {
  const walletId = form?.values?._id?.toString?.() ?? form?.values?._id;
  const [api] = useServerApi();
  const [formatter] = useFormatter();
  const [statusTab, setStatusTab] = useState("ALL");
  const [bids, setBids] = useState([]);
  const [initialLoading, setInitialLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalDocs, setTotalDocs] = useState(0);
  const apiRef = useRef(api);
  apiRef.current = api;

  useEffect(() => {
    if (!walletId) {
      setBids([]);
      setTotalDocs(0);
      setCurrentPage(1);
      return;
    }

    let cancelled = false;

    const loadBids = async () => {
      setInitialLoading(true);
      try {
        const result = await apiRef.current.search({
          apiEntity: "payrollBid",
          searchQuery: buildSearchQuery(walletId, statusTab),
          pageSize: PAGE_SIZE,
          currentPage: 1,
          sort: { by: "createdAt", order: "desc" },
          byAggregation: false,
        });

        if (cancelled) return;

        const docs = result?.docs ?? [];
        setBids(docs);
        setTotalDocs(result?.total ?? docs.length);
        setCurrentPage(1);
      } catch (error) {
        if (!cancelled) {
          console.error("Error fetching wallet payroll bids:", error);
          setBids([]);
          setTotalDocs(0);
        }
      } finally {
        if (!cancelled) setInitialLoading(false);
      }
    };

    loadBids();

    return () => {
      cancelled = true;
    };
  }, [walletId, statusTab]);

  const loadMore = async () => {
    if (!walletId || loadingMore) return;

    setLoadingMore(true);
    try {
      const nextPage = currentPage + 1;
      const result = await apiRef.current.search({
        apiEntity: "payrollBid",
        searchQuery: buildSearchQuery(walletId, statusTab),
        pageSize: PAGE_SIZE,
        currentPage: nextPage,
        sort: { by: "createdAt", order: "desc" },
        byAggregation: false,
      });

      const docs = result?.docs ?? [];
      setBids((prev) => [...prev, ...docs]);
      setCurrentPage(nextPage);
      setTotalDocs(result?.total ?? totalDocs);
    } catch (error) {
      console.error("Error loading more wallet payroll bids:", error);
    } finally {
      setLoadingMore(false);
    }
  };

  const summary = useMemo(() => buildStatusSummary(bids), [bids]);
  const hasMore = bids.length < totalDocs;

  if (!form?.values) {
    return (
      <Group position="center" py="md">
        <Loader size="sm" />
      </Group>
    );
  }

  if (!walletId) {
    return (
      <Text size="sm" color="dimmed">
        Save the wallet to view payroll bids.
      </Text>
    );
  }

  return (
    <Stack spacing="md">
      <Tabs variant="outline" value={statusTab} onTabChange={setStatusTab}>
        <Tabs.List>
          {STATUS_TABS.map((status) => (
            <Tabs.Tab key={status} value={status}>
              {status === "ALL" ? "All" : status}
            </Tabs.Tab>
          ))}
        </Tabs.List>
      </Tabs>

      <Group spacing="lg">
        <Text size="sm">
          <Text component="span" color="dimmed">
            Total bids:{" "}
          </Text>
          <Text component="span" weight={600}>
            {summary.total}
          </Text>
        </Text>
        <Text size="sm">
          <Text component="span" color="dimmed">
            Total amount:{" "}
          </Text>
          <Text component="span" weight={600}>
            {formatter.currency(summary.totalAmount)}
          </Text>
        </Text>
        {Object.entries(summary.counts).map(([status, count]) =>
          count > 0 ? (
            <Badge key={status} size="sm" color={getStatusColor(status)}>
              {status}: {count}
            </Badge>
          ) : null,
        )}
      </Group>

      {initialLoading && bids.length === 0 ? (
        <Group position="center" py="md">
          <Loader size="sm" />
        </Group>
      ) : bids.length === 0 ? (
        <Text size="sm" color="dimmed">
          No payroll bids found for this wallet.
        </Text>
      ) : (
        <>
          <Table fontSize="xs" striped highlightOnHover withBorder>
            <thead>
              <tr>
                <th style={{ width: 36 }}>#</th>
                <th>Payroll</th>
                <th>Bid</th>
                <th>Source</th>
                <th>Order</th>
                <th>Debit Note</th>
                <th style={{ textAlign: "right" }}>Amount</th>
              </tr>
            </thead>
            <tbody>
              {bids.map((bid, index) => {
                const amount = bid.commissionAmount ?? 0;
                return (
                  <tr key={bid._id || index}>
                    <td>{index + 1}</td>
                    <td>
                      <EntityTabAnchor
                        entity="payroll"
                        id={bid.payroll?._id || bid.payroll}
                        label={getPayrollLabel(bid)}
                      />
                    </td>
                    <td>
                      <Stack spacing={4} align="flex-start">
                        <Badge size="xs" color={getStatusColor(bid.status)}>
                          {bid.status || "PENDING"}
                        </Badge>
                        <Text size="xs" weight={600}>
                          {bid.code || "-"}
                        </Text>
                      </Stack>
                    </td>
                    <td>
                      <Badge
                        size="xs"
                        color={getSourceColor(bid)}
                        variant="light"
                      >
                        {getSourceLabel(bid)}
                      </Badge>
                    </td>
                    <td>
                      <EntityTabAnchor
                        entity="order"
                        id={bid.order?._id || bid.order}
                        label={bid.order?.code}
                      />
                    </td>
                    <td>
                      <EntityTabAnchor
                        entity="debitNote"
                        id={bid.debitNote?._id || bid.debitNote}
                        label={bid.debitNote?.code}
                      />
                    </td>
                    <td style={{ textAlign: "right" }}>
                      <Text
                        size="xs"
                        weight={500}
                        color={amount >= 0 ? "green" : "red"}
                      >
                        {formatter.currency(amount)}
                      </Text>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>

          {hasMore && (
            <Group position="center">
              <Button
                size="xs"
                variant="light"
                loading={loadingMore}
                onClick={loadMore}
              >
                Load more ({bids.length} of {totalDocs})
              </Button>
            </Group>
          )}
        </>
      )}
    </Stack>
  );
};

export default WalletPayrollBids;
